import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useDataChannel,
  useIsSpeaking,
  useLocalParticipant,
  useRemoteParticipants
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaraAvatar } from './sara-avatar';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import {
  InterviewApiError,
  connectVoiceInterview,
  restartInterview,
  submitVoiceInterview
} from '~/lib/interview-api';

interface VoiceInterfaceProps {
  token: string;
  language: string;
  jobTitle: string;
  employer: string | null;
  candidateName: string;
  onCompleted: () => void;
  onRestart?: () => void;
}

export function VoiceInterface({
  token,
  language,
  jobTitle,
  employer,
  candidateName,
  onCompleted,
  onRestart
}: VoiceInterfaceProps): React.ReactNode {
  const { t } = useTranslation();
  const [connectionInfo, setConnectionInfo] = useState<{
    participantToken: string;
    livekitUrl: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);

  useEffect(() => {
    async function connect(): Promise<void> {
      try {
        const info = await connectVoiceInterview(token, language);
        setConnectionInfo(info);
        setConnecting(false);
      } catch (err) {
        // 409 means the session was already claimed by the other strict-mode mount — ignore
        if (err instanceof InterviewApiError && err.status === 409) return;
        setError(err instanceof Error ? err.message : 'Failed to connect');
        setConnecting(false);
      }
    }

    void connect();
  }, [token, language]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (connecting || !connectionInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('screening.voice.connecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={connectionInfo.participantToken}
      serverUrl={connectionInfo.livekitUrl}
      connect={true}
      audio={true}
      video={false}
    >
      <VoiceSession
        token={token}
        jobTitle={jobTitle}
        employer={employer}
        candidateName={candidateName}
        onCompleted={onCompleted}
        onRestart={onRestart}
      />
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function ParticipantCard({
  name,
  initials,
  isSpeaking,
  variant
}: {
  name: string;
  initials: string;
  isSpeaking: boolean;
  variant: 'ai' | 'candidate';
}): React.ReactNode {
  const speakingRing = variant === 'ai' ? 'ring-primary/40' : 'ring-foreground/20';

  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center gap-4 rounded-2xl border bg-card p-8 transition-shadow ${
        isSpeaking ? `ring-[3px] ${speakingRing}` : 'border-border'
      }`}
    >
      {variant === 'ai' ? (
        <SaraAvatar size="lg" />
      ) : (
        <div className="flex size-24 items-center justify-center rounded-full bg-muted text-2xl font-semibold text-muted-foreground">
          {initials}
        </div>
      )}
      <span className="text-sm font-medium text-foreground">{name}</span>
    </div>
  );
}

function VoiceSession({
  token,
  jobTitle,
  employer,
  candidateName,
  onCompleted,
  onRestart
}: {
  token: string;
  jobTitle: string;
  employer: string | null;
  candidateName: string;
  onCompleted: () => void;
  onRestart?: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const [interviewFinished, setInterviewFinished] = useState(false);
  const [wasConnected, setWasConnected] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [restartNotes, setRestartNotes] = useState('');
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const isConnected = connectionState === ConnectionState.Connected;

  const agentParticipant = remoteParticipants[0] ?? localParticipant;
  const localIsSpeaking = useIsSpeaking(localParticipant);
  const agentIsSpeaking = useIsSpeaking(agentParticipant);
  const hasAgent = remoteParticipants.length > 0;

  const candidateInitials = useMemo(() => getInitials(candidateName), [candidateName]);

  // Listen for completion data message from agent
  useDataChannel('interview-status', (msg) => {
    try {
      const data: unknown = JSON.parse(new TextDecoder().decode(msg.payload));
      if ((data as { type: string }).type === 'interview_complete') {
        setInterviewFinished(true);
      }
    } catch {
      // ignore malformed data
    }
  });

  // Track that we were connected at least once
  useEffect(() => {
    if (isConnected) setWasConnected(true);
  }, [isConnected]);

  // Fallback: if the room disconnects after being connected, show submit button
  useEffect(() => {
    if (wasConnected && connectionState === ConnectionState.Disconnected) {
      setInterviewFinished(true);
    }
  }, [wasConnected, connectionState]);

  function handleCloseRestartModal(): void {
    if (isRestarting) return;
    setShowRestartModal(false);
    setRestartNotes('');
    setRestartError(null);
  }

  async function handleRestart(): Promise<void> {
    setIsRestarting(true);
    setRestartError(null);
    try {
      await restartInterview(token, restartNotes, 'technical_issue');
      setShowRestartModal(false);
      onRestart?.();
    } catch {
      setRestartError(t('screening.technicalIssue.submitError'));
      setIsRestarting(false);
    }
  }

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
        <div>
          <p className="text-lg font-semibold">{t('screening.voice.interviewInProgress')}</p>
          <p className="text-xs text-muted-foreground">
            {jobTitle}
            {employer ? ` — ${employer}` : ''}
          </p>
        </div>
        {isConnected && !interviewFinished && (
          <div className="flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1">
            <span className="size-2 rounded-full bg-green-500" />
            <span className="text-xs font-medium text-green-700">
              {t('screening.voice.recording')}
            </span>
          </div>
        )}
      </div>

      {/* Main area */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:px-6">
        {!isConnected && !interviewFinished && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('screening.voice.connecting')}
          </div>
        )}

        {(isConnected || interviewFinished) && (
          <div className="grid w-full max-w-3xl flex-1 grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <ParticipantCard
              name={candidateName}
              initials={candidateInitials}
              isSpeaking={!interviewFinished && localIsSpeaking}
              variant="candidate"
            />
            <ParticipantCard
              name="Sara"
              initials="SA"
              isSpeaking={!interviewFinished && hasAgent && agentIsSpeaking}
              variant="ai"
            />
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border px-4 py-3 sm:px-6">
        <div className="relative mx-auto flex max-w-3xl items-center justify-end">
          {/* Left: technical issue link */}
          <button
            className="absolute left-0 text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => {
              setShowRestartModal(true);
            }}
          >
            {t('screening.technicalIssue.buttonLabel')}
          </button>

          {/* Right: submit button (always visible) */}
          <Button
            className="h-11 gap-2 px-6"
            onClick={() => {
              setShowSubmitConfirm(true);
            }}
          >
            <CheckCircle className="size-4" />
            {t('screening.submitInterview')}
          </Button>
        </div>

        {/* AI disclosure */}
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {t('screening.aiDisclosure')}
        </p>
      </div>

      <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('screening.voice.submitConfirm.title')}</DialogTitle>
            <DialogDescription>{t('screening.voice.submitConfirm.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSubmitConfirm(false);
              }}
            >
              {t('screening.voice.submitConfirm.cancel')}
            </Button>
            <Button
              onClick={() => {
                void submitVoiceInterview(token)
                  .catch(() => {
                    // best-effort — proceed even if the flag fails to set
                  })
                  .finally(() => {
                    onCompleted();
                  });
              }}
            >
              {t('screening.voice.submitConfirm.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showRestartModal}
        onOpenChange={(open) => {
          if (!open) handleCloseRestartModal();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('screening.technicalIssue.modalTitle')}</DialogTitle>
            <DialogDescription>{t('screening.technicalIssue.modalDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('screening.technicalIssue.notesLabel')}
            </label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={4}
              maxLength={2000}
              placeholder={t('screening.technicalIssue.notesPlaceholder')}
              value={restartNotes}
              onChange={(e) => {
                setRestartNotes(e.target.value);
              }}
              disabled={isRestarting}
            />
            {restartError && <p className="text-sm text-destructive">{restartError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseRestartModal} disabled={isRestarting}>
              {t('screening.technicalIssue.cancel')}
            </Button>
            <Button
              onClick={() => {
                void handleRestart();
              }}
              disabled={isRestarting || !restartNotes.trim()}
            >
              {t('screening.technicalIssue.submitButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
